import type React from "react";

import { useCallback, useEffect, useRef, useState } from "react";

import { useAtom } from "jotai";

import { useTranslation } from "react-i18next";

import { useNavigate, useLocation } from "react-router-dom";

import {
  emitToggleGridLockEvent,
  emitAddWidgetEvent,
  emitOpenStreamSettingsEvent,
} from "../../Utils/navigationEvents";

import { atomGridLocked } from "../../screens/Dashboard/SubComponents/Widgets/Widgets.atoms";

import {
  atomGlobalAudioLocalOn,
  atomGlobalAudioStreamingOn,
} from "../GlobalAudioWidget/GlobalAudioWidget.atoms";

import { atomOpenMenu } from "./MenuBar.atoms";

import Radio from "../../entities/Radio";

import { useUserType } from "./CustomTitleBar.hooks";

import ReportProblemModal from "../ReportProblemModal/ReportProblemModal";

export interface MenuBarProps {
  isAuthenticated?: boolean;

  loading: boolean;
}

export const MenuBar: React.FC<MenuBarProps> = ({ isAuthenticated = false, loading = true }) => {
  const { t } = useTranslation("dashboard");

  const navigate = useNavigate();

  const location = useLocation();

  const [openMenu, setOpenMenu] = useAtom(atomOpenMenu);

  const menuRef = useRef<HTMLDivElement>(null);

  const [gridLocked] = useAtom(atomGridLocked);

  const [localOn, setLocalOn] = useAtom(atomGlobalAudioLocalOn);

  const [streamingOn] = useAtom(atomGlobalAudioStreamingOn);

  const { isLocalRecording } = useUserType();

  const [reportOpen, setReportOpen] = useState(false);

  const pathname = location.pathname;

  const handleMenuClick = useCallback(
    (menuId: string) => {
      setOpenMenu(openMenu === menuId ? null : menuId);
    },

    [openMenu, setOpenMenu]
  );

  const closeMenu = useCallback(() => {
    setOpenMenu(null);
  }, [setOpenMenu]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenu(null);
      }
    };

    if (openMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openMenu, setOpenMenu]);

  const handleNavigate = useCallback(
    (path: string) => {
      setOpenMenu(null);

      navigate(path);
    },

    [setOpenMenu, navigate]
  );

  const handleToggleLocalAudio = useCallback(async () => {
    const radio = new Radio();

    const currentLocal = await radio.GlobalAudio.isLocalDevicePlaying();

    if (currentLocal) {
      setLocalOn(false);

      await radio.GlobalAudio.pause();
    } else {
      setLocalOn(true);

      await radio.GlobalAudio.resume();
    }

    closeMenu();
  }, [setLocalOn, closeMenu]);

  const handleStreamingInfo = useCallback(() => {
    closeMenu();
  }, [closeMenu]);

  const menuItemClass = (active: boolean) =>
    `relative px-2.5 h-full flex items-center text-[13px] text-foreground cursor-pointer select-none ${
      active ? "bg-accent" : "hover:bg-accent"
    }`;

  const dropdownItemClass =
    "py-2 px-3 text-[13px] text-muted-foreground cursor-pointer rounded hover:bg-accent hover:text-foreground active:bg-accent/80 select-none";

  return (
    <div className="titlebar-nodrag flex items-center gap-1 ml-3" ref={menuRef}>
      {/* Streaming Menu */}
      {pathname === "/dashboard" && !isLocalRecording && (
        <div
          className={menuItemClass(openMenu === "streaming")}
          data-active={openMenu === "streaming"}
          onClick={() => handleMenuClick("streaming")}
        >
          {t("DASHBOARD.MENUBAR.streaming")}
          {openMenu === "streaming" && (
            <div className="titlebar-dropdown absolute top-full left-0 min-w-[200px] mt-0.5 p-1 z-[10001]">
              <div
                className={dropdownItemClass}
                onClick={() => {
                  emitOpenStreamSettingsEvent();

                  closeMenu();
                }}
              >
                {t("DASHBOARD.MENUBAR.STREAMING.configure")}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Navigation Menu */}
      {!loading && isAuthenticated && (
        <div
          className={menuItemClass(openMenu === "go")}
          data-active={openMenu === "go"}
          onClick={() => handleMenuClick("go")}
        >
          {t("DASHBOARD.MENUBAR.go")}
          {openMenu === "go" && (
            <div className="titlebar-dropdown absolute top-full left-0 min-w-[200px] mt-0.5 p-1 z-[10001]">
              <div className={dropdownItemClass} onClick={() => handleNavigate("/dashboard")}>
                {t("DASHBOARD.MENUBAR.GO.dashboard")}
              </div>
              {isLocalRecording ? (
                <div
                  className={dropdownItemClass}
                  onClick={() => handleNavigate("/local-recordings")}
                >
                  {t("DASHBOARD.MENUBAR.GO.recordings")}
                </div>
              ) : (
                <div className={dropdownItemClass} onClick={() => handleNavigate("/live-streams")}>
                  {t("DASHBOARD.MENUBAR.GO.liveStreams")}
                </div>
              )}
              <div className={dropdownItemClass} onClick={() => handleNavigate("/workspace")}>
                {t("DASHBOARD.MENUBAR.GO.workspace")}
              </div>
              <div className={dropdownItemClass} onClick={() => handleNavigate("/simulated-live")}>
                Simulated Live
              </div>
              <div className={dropdownItemClass} onClick={() => handleNavigate("/stats")}>
                {t("DASHBOARD.MENUBAR.GO.stats")}
              </div>
            </div>
          )}
        </div>
      )}

      {/* View Menu */}
      {pathname === "/dashboard" && (
        <div
          className={menuItemClass(openMenu === "view")}
          data-active={openMenu === "view"}
          onClick={() => handleMenuClick("view")}
        >
          {t("DASHBOARD.MENUBAR.view")}
          {openMenu === "view" && (
            <div className="titlebar-dropdown absolute top-full left-0 min-w-[200px] mt-0.5 p-1 z-[10001]">
              <div
                className={dropdownItemClass}
                onClick={() => {
                  emitToggleGridLockEvent();

                  closeMenu();
                }}
              >
                {gridLocked
                  ? t("DASHBOARD.MENUBAR.VIEW.unlockGrid")
                  : t("DASHBOARD.MENUBAR.VIEW.lockGrid")}
              </div>
              <div className="h-px bg-border mx-2 my-1" />
              <div
                className={dropdownItemClass}
                onClick={() => {
                  emitAddWidgetEvent();

                  closeMenu();
                }}
              >
                {t("DASHBOARD.MENUBAR.VIEW.addWidget")}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Audio Menu */}
      {pathname === "/dashboard" && (
        <div
          className={menuItemClass(openMenu === "audio")}
          data-active={openMenu === "audio"}
          onClick={() => handleMenuClick("audio")}
        >
          {t("DASHBOARD.MENUBAR.audio")}
          {openMenu === "audio" && (
            <div className="titlebar-dropdown absolute top-full left-0 min-w-[200px] mt-0.5 p-1 z-[10001]">
              <div className={dropdownItemClass} onClick={handleToggleLocalAudio}>
                {localOn
                  ? t("DASHBOARD.MENUBAR.AUDIO.disableLocalAudio")
                  : t("DASHBOARD.MENUBAR.AUDIO.enableLocalAudio")}
              </div>
              <div
                className={`${dropdownItemClass} opacity-70 !cursor-default`}
                onClick={handleStreamingInfo}
              >
                {streamingOn
                  ? t("DASHBOARD.MENUBAR.AUDIO.streamingOn")
                  : t("DASHBOARD.MENUBAR.AUDIO.streamingOff")}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Help Menu — always available */}
      <div
        className={menuItemClass(openMenu === "help")}
        data-active={openMenu === "help"}
        onClick={() => handleMenuClick("help")}
      >
        {t("DASHBOARD.MENUBAR.help", "Ayuda")}
        {openMenu === "help" && (
          <div className="titlebar-dropdown absolute top-full left-0 min-w-[200px] mt-0.5 p-1 z-[10001]">
            <div
              className={dropdownItemClass}
              onClick={() => {
                setReportOpen(true);
                closeMenu();
              }}
            >
              {t("DASHBOARD.MENUBAR.HELP.reportProblem", "Reportar un problema")}
            </div>
          </div>
        )}
      </div>

      <ReportProblemModal
        isOpen={reportOpen}
        onClose={() => setReportOpen(false)}
        source="menu-bar"
      />
    </div>
  );
};
